import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { chooseDBTranslation } from 'utils/translations'
import { convertToDisplayDate } from 'utils/utils'
import {
  IProject,
  IEmployer,
  IEducation,
  ICourse,
  IPersonCertificate,
  IPersonCompetence,
  ILeisureActivity,
  IReference,
  IUrl,
} from 'types/cvsInterfaces'
import colors from 'constants/colors'
import { ICVData, ILayout } from 'types/layoutInterfaces'
import Grid from '@mui/material/Grid2'
import { useUser } from 'hooks'
import { printFontFamily } from './PrintFontFaces'
import { findHideSetting, isDetailHidden } from '../hideSettings'

/**
 * Renders a data layout component.
 *
 * @param {any} data - The data to be rendered.
 * @param {string} type - The type of data.
 * @param {string} headingColor - The color of the heading.
 * @returns {React.ReactElement} The rendered DataLayout component.
 * @notExported
 */
const DataLayout: React.FC<{ data: ICVData; type: string; headingColor: string; layout?: ILayout }> = ({
  data,
  type,
  headingColor,
  layout,
}) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const { features } = useUser()
  const hideSetting = findHideSetting(layout, type)

  const hidden = useMemo(() => {
    const sectionItems = data[type as keyof ICVData] as unknown[] | undefined
    if (sectionItems && sectionItems.length) {
      return false
    }
    return true
  }, [data, type])

  const items: React.ReactNode[] | undefined = useMemo(() => {
    switch (type) {
      case 'projects':
        return data.projects.map((item: IProject, index: number) => {
          return (
            <Grid key={item.id} sx={{ width: '100%', marginBottom: '20pt' }}>
              {index === 0 ? (
                <Grid
                  sx={{
                    fontSize: '18.8pt',
                    fontFamily: printFontFamily,
                    fontWeight: 800,
                    marginBottom: '8pt',
                    letterSpacing: 0,
                    borderBottom: `2pt solid ${theme.palette.neutral.dark}`,
                    marginTop: '18pt',
                    minWidth: '100%',
                    color: headingColor ? headingColor : colors.secondary,
                  }}
                >
                  {t(`print.projects.title`)}
                </Grid>
              ) : null}
              <Grid sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', justifyContent: 'space-between' }}>
                {!isDetailHidden(hideSetting, 'name') && (
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {chooseDBTranslation(i18n, item).name}
                  </Box>
                )}
                {!isDetailHidden(hideSetting, 'date') && (
                  <Box sx={{ fontSize: '10.1pt', lineHeight: 1.5 }}>
                    {convertToDisplayDate(item.startDate, item.dateResolution)} -{' '}
                    {item.endDate ? convertToDisplayDate(item.endDate, item.dateResolution) : null}
                  </Box>
                )}
              </Grid>
              {!isDetailHidden(hideSetting, 'primaryRole') && (
                <>
                  {chooseDBTranslation(i18n, item).primaryRole.length > 0 && (
                    <Grid sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginTop: '5pt' }}>
                      <Box
                        sx={{
                          fontSize: '11.8pt',
                          fontFamily: printFontFamily,
                          fontWeight: 'bold',
                          lineHeight: 1.7,
                          marginRight: '5px',
                          color: theme.palette.neutral.dark,
                        }}
                      >
                        {t('print.project.role')}:{' '}
                      </Box>
                      <Box sx={{ fontSize: '11.8pt', color: theme.palette.neutral.dark, lineHeight: 1.7 }}>
                        {chooseDBTranslation(i18n, item)?.primaryRole}
                      </Box>
                    </Grid>
                  )}
                </>
              )}
              {!isDetailHidden(hideSetting, 'projectRoles') && (
                <>
                  {item.ProjectRoles && item.ProjectRoles.length > 0 && (
                    <Grid sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginTop: '5pt' }}>
                      <Box
                        sx={{
                          fontSize: '11.8pt',
                          fontFamily: printFontFamily,
                          fontWeight: 'bold',
                          lineHeight: 1.7,
                          marginRight: '5px',
                          color: theme.palette.neutral.dark,
                        }}
                      >
                        {t('print.project.roles')}:{' '}
                      </Box>
                      {item.ProjectRoles.map(role => (
                        <Box
                          key={role.id}
                          sx={{
                            fontSize: '11.8pt',
                            lineHeight: 1.7,
                            color: theme.palette.neutral.dark,
                            marginRight: '15pt',
                          }}
                        >
                          {chooseDBTranslation(i18n, role.Skill).name}
                        </Box>
                      ))}
                    </Grid>
                  )}
                </>
              )}
              {!isDetailHidden(hideSetting, 'customerName') && (
                <Grid
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    marginRight: '15pt',
                    minWidth: '100%',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {t('print.project.customer')}:{' '}
                  </Box>
                  <Box sx={{ fontSize: '11.8pt', color: theme.palette.neutral.dark, lineHeight: 1.7 }}>
                    {chooseDBTranslation(i18n, item)?.customerName}
                  </Box>
                </Grid>
              )}
              {!isDetailHidden(hideSetting, 'employerName') && (
                <Grid
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    marginRight: '15pt',
                    minWidth: '100%',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {t('print.project.employer')}:{' '}
                  </Box>
                  <Box sx={{ fontSize: '11.8pt', color: theme.palette.neutral.dark, lineHeight: 1.7 }}>
                    {chooseDBTranslation(i18n, item)?.employerName}
                  </Box>
                </Grid>
              )}
              {!isDetailHidden(hideSetting, 'personSkills') && (
                <>
                  {item.PersonSkills && item.PersonSkills?.length > 0 ? (
                    <Grid sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginTop: '5pt' }}>
                      <Box
                        sx={{
                          fontSize: '11.8pt',
                          fontFamily: printFontFamily,
                          fontWeight: 'bold',
                          lineHeight: 1.7,
                          marginRight: '5px',
                          color: theme.palette.neutral.dark,
                        }}
                      >
                        {t('print.project.skills')}:{' '}
                      </Box>
                      {item.PersonSkills.map(skill => (
                        <Box
                          key={skill.id}
                          sx={{
                            fontSize: '11.8pt',
                            lineHeight: 1.7,
                            color: theme.palette.neutral.dark,
                            marginRight: '15pt',
                          }}
                        >
                          {chooseDBTranslation(i18n, skill.Skill).name}
                        </Box>
                      ))}
                    </Grid>
                  ) : null}
                </>
              )}
              {!isDetailHidden(hideSetting, 'industries') && (
                <>
                  {item.Industries && item.Industries?.length > 0 ? (
                    <Grid sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginTop: '5pt' }}>
                      <Box
                        sx={{
                          fontSize: '11.8pt',
                          fontFamily: printFontFamily,
                          fontWeight: 'bold',
                          lineHeight: 1.7,
                          marginRight: '5px',
                          color: theme.palette.neutral.dark,
                        }}
                      >
                        {t('print.project.industries')}:{' '}
                      </Box>
                      {item.Industries.map(industry => (
                        <Box
                          key={industry.id}
                          sx={{
                            fontSize: '11.8pt',
                            lineHeight: 1.7,
                            color: theme.palette.neutral.dark,
                            marginRight: '15pt',
                          }}
                        >
                          {chooseDBTranslation(i18n, industry.Skill).name}
                        </Box>
                      ))}
                    </Grid>
                  ) : null}
                </>
              )}
              {!isDetailHidden(hideSetting, 'description') && (
                <Box
                  sx={{
                    fontSize: '9.7pt',
                    color: theme.palette.neutral.dark,
                    whiteSpace: 'pre-wrap',
                    textAlign: 'justify',
                  }}
                >
                  {chooseDBTranslation(i18n, item).description}
                </Box>
              )}
            </Grid>
          )
        })

      case 'employments':
        return data.employments.map((item: IEmployer, index: number) => {
          return (
            <Grid key={item.id} sx={{ width: '100%', marginBottom: '20pt' }}>
              {index === 0 ? (
                <Grid
                  sx={{
                    fontSize: '18.8pt',
                    fontFamily: printFontFamily,
                    fontWeight: 800,
                    marginBottom: '8pt',
                    color: theme.palette.neutral.dark,
                    letterSpacing: 0,
                    borderBottom: `2pt solid ${theme.palette.neutral.dark}`,
                    marginTop: '18pt',
                    minWidth: '100%',
                  }}
                  style={{ color: headingColor ? headingColor : colors.secondary }}
                >
                  {t(`print.employments.title`)}
                </Grid>
              ) : null}
              <Grid sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', justifyContent: 'space-between' }}>
                {!isDetailHidden(hideSetting, 'name') && (
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {chooseDBTranslation(i18n, item).name}
                  </Box>
                )}
                {!isDetailHidden(hideSetting, 'date') && (
                  <Box sx={{ fontSize: '10.1pt', lineHeight: 1.5 }}>
                    {convertToDisplayDate(item.startDate, item.dateResolution)} -{' '}
                    {item.endDate ? convertToDisplayDate(item.endDate, item.dateResolution) : null}
                  </Box>
                )}
              </Grid>
              {!isDetailHidden(hideSetting, 'primaryRole') && (
                <>
                  {chooseDBTranslation(i18n, item).primaryRole.length > 0 && (
                    <Grid sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginTop: '5pt' }}>
                      <Box
                        sx={{
                          fontSize: '11.8pt',
                          fontFamily: printFontFamily,
                          fontWeight: 'bold',
                          lineHeight: 1.7,
                          marginRight: '5px',
                          color: theme.palette.neutral.dark,
                        }}
                      >
                        {t('print.project.role')}:{' '}
                      </Box>
                      <Box sx={{ fontSize: '11.8pt', color: theme.palette.neutral.dark, lineHeight: 1.7 }}>
                        {chooseDBTranslation(i18n, item)?.primaryRole}
                      </Box>
                    </Grid>
                  )}
                </>
              )}
              {!isDetailHidden(hideSetting, 'roles') && (
                <>
                  {item.EmployerRoles && item.EmployerRoles?.length > 0 && (
                    <Grid sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', marginTop: '5pt' }}>
                      <Box
                        sx={{
                          fontSize: '11.8pt',
                          fontFamily: printFontFamily,
                          fontWeight: 'bold',
                          lineHeight: 1.7,
                          marginRight: '5px',
                          color: theme.palette.neutral.dark,
                        }}
                      >
                        {t('print.employment.roles')}:{' '}
                      </Box>
                      {item.EmployerRoles.map(role => (
                        <Box
                          key={role.id}
                          sx={{
                            fontSize: '11.8pt',
                            lineHeight: 1.7,
                            color: theme.palette.neutral.dark,
                            marginRight: '15pt',
                          }}
                        >
                          {chooseDBTranslation(i18n, role.Skill).name}
                        </Box>
                      ))}
                    </Grid>
                  )}
                </>
              )}
              {!isDetailHidden(hideSetting, 'description') && (
                <Box
                  sx={{
                    fontSize: '9.7pt',
                    color: theme.palette.neutral.dark,
                    whiteSpace: 'pre-wrap',
                    textAlign: 'justify',
                  }}
                >
                  {chooseDBTranslation(i18n, item).description}
                </Box>
              )}
            </Grid>
          )
        })

      case 'degrees':
        return data.degrees.map((item: IEducation, index: number) => {
          let date = ''
          if (item.startDate) {
            date += `${convertToDisplayDate(item.startDate, item.dateResolution)} - `
          }
          if (item.endDate) {
            date += `${convertToDisplayDate(item.endDate, item.dateResolution)}`
          }

          return (
            <Grid key={item.id} sx={{ width: '100%', marginBottom: '20pt' }}>
              {index === 0 ? (
                <Grid
                  sx={{
                    fontSize: '18.8pt',
                    fontFamily: printFontFamily,
                    fontWeight: 800,
                    marginBottom: '8pt',
                    color: theme.palette.neutral.dark,
                    letterSpacing: 0,
                    borderBottom: `2pt solid ${theme.palette.neutral.dark}`,
                    marginTop: '18pt',
                    minWidth: '100%',
                  }}
                  style={{ color: headingColor ? headingColor : colors.secondary }}
                >
                  {t(`print.degrees.title`)}
                </Grid>
              ) : null}
              <Grid sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', justifyContent: 'space-between' }}>
                {!isDetailHidden(hideSetting, 'name') && (
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {chooseDBTranslation(i18n, item).school}
                  </Box>
                )}
                {!isDetailHidden(hideSetting, 'date') && <Box sx={{ fontSize: '10.1pt', lineHeight: 1.5 }}>{date}</Box>}
              </Grid>
              {!isDetailHidden(hideSetting, 'degree') && (
                <Grid
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    marginRight: '15pt',
                    minWidth: '100%',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {t('print.degree')}:{' '}
                  </Box>
                  <Box sx={{ fontSize: '11.8pt', color: theme.palette.neutral.dark, lineHeight: 1.7 }}>
                    {chooseDBTranslation(i18n, item).degree}
                  </Box>
                </Grid>
              )}
              {!isDetailHidden(hideSetting, 'description') && (
                <Box
                  sx={{
                    fontSize: '9.7pt',
                    color: theme.palette.neutral.dark,
                    whiteSpace: 'pre-wrap',
                    textAlign: 'justify',
                  }}
                >
                  {chooseDBTranslation(i18n, item).description}
                </Box>
              )}
            </Grid>
          )
        })

      case 'courses':
        return data.courses.map((item: ICourse, index: number) => {
          let date = ''
          if (item.startDate) {
            date += `${convertToDisplayDate(item.startDate, item.dateResolution)} - `
          }
          if (item.endDate) {
            date += `${convertToDisplayDate(item.endDate, item.dateResolution)}`
          }

          return (
            <Grid key={item.id} sx={{ width: '100%', marginBottom: '20pt' }}>
              {index === 0 ? (
                <Grid
                  sx={{
                    fontSize: '18.8pt',
                    fontFamily: printFontFamily,
                    fontWeight: 800,
                    marginBottom: '8pt',
                    color: theme.palette.neutral.dark,
                    letterSpacing: 0,
                    borderBottom: `2pt solid ${theme.palette.neutral.dark}`,
                    marginTop: '18pt',
                    minWidth: '100%',
                  }}
                  style={{ color: headingColor ? headingColor : colors.secondary }}
                >
                  {t(`print.courses.title`)}
                </Grid>
              ) : null}
              <Grid sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', justifyContent: 'space-between' }}>
                {!isDetailHidden(hideSetting, 'name') && (
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {chooseDBTranslation(i18n, item).course}
                  </Box>
                )}
                {!isDetailHidden(hideSetting, 'date') && <Box sx={{ fontSize: '10.1pt', lineHeight: 1.5 }}>{date}</Box>}
              </Grid>
              {!isDetailHidden(hideSetting, 'organizer') && (
                <Grid
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    marginRight: '15pt',
                    minWidth: '100%',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {t('print.organizer')}:{' '}
                  </Box>
                  <Box sx={{ fontSize: '11.8pt', color: theme.palette.neutral.dark, lineHeight: 1.7 }}>
                    {chooseDBTranslation(i18n, item).organizer}
                  </Box>
                </Grid>
              )}
              {!isDetailHidden(hideSetting, 'description') && (
                <Box
                  sx={{
                    fontSize: '9.7pt',
                    color: theme.palette.neutral.dark,
                    whiteSpace: 'pre-wrap',
                    textAlign: 'justify',
                  }}
                >
                  {chooseDBTranslation(i18n, item).description}
                </Box>
              )}
            </Grid>
          )
        })

      case 'personCertificates':
        return data.personCertificates.map((item: IPersonCertificate, index: number) => {
          let date = ''
          if (item.issueDate) {
            date += `${convertToDisplayDate(item.issueDate, item.dateResolution)}`
          }
          let endDate = ''
          if (item.endDate) {
            endDate += `${convertToDisplayDate(item.endDate, item.dateResolution)}`
          }

          return (
            <Grid key={item.id} sx={{ width: '100%', marginBottom: '20pt' }}>
              {index === 0 ? (
                <Grid
                  sx={{
                    fontSize: '18.8pt',
                    fontFamily: printFontFamily,
                    fontWeight: 800,
                    marginBottom: '8pt',
                    color: theme.palette.neutral.dark,
                    letterSpacing: 0,
                    borderBottom: `2pt solid ${theme.palette.neutral.dark}`,
                    marginTop: '18pt',
                    minWidth: '100%',
                  }}
                  style={{ color: headingColor ? headingColor : colors.secondary }}
                >
                  {t(`print.certificates.title`)}
                </Grid>
              ) : null}
              <Grid sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', justifyContent: 'space-between' }}>
                {!isDetailHidden(hideSetting, 'name') && (
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {item.Certificate && chooseDBTranslation(i18n, item.Certificate).certificate}
                  </Box>
                )}
                {!isDetailHidden(hideSetting, 'date') && <Box sx={{ fontSize: '10.1pt', lineHeight: 1.5 }}>{date}</Box>}
              </Grid>
              {!isDetailHidden(hideSetting, 'certifier') && (
                <Grid
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    marginRight: '15pt',
                    minWidth: '100%',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {t('print.certifier')}:{' '}
                  </Box>
                  <Box sx={{ fontSize: '11.8pt', color: theme.palette.neutral.dark, lineHeight: 1.7 }}>
                    {chooseDBTranslation(i18n, item).certifier}
                  </Box>
                </Grid>
              )}
              {endDate.length > 0 && layout && layout.showCertificateExpiry && (
                <Grid
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    marginRight: '15pt',
                    minWidth: '100%',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {t('print.certificateExpiry')}:{' '}
                  </Box>
                  <Box sx={{ fontSize: '11.8pt', color: theme.palette.neutral.dark, lineHeight: 1.7 }}>{endDate}</Box>
                </Grid>
              )}
              {item.url && !isDetailHidden(hideSetting, 'url') && (
                <Grid
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    marginRight: '15pt',
                    minWidth: '100%',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {t('print.url')}:{' '}
                  </Box>
                  <Box sx={{ fontSize: '11.8pt', color: theme.palette.neutral.dark, lineHeight: 1.7 }}>{item.url}</Box>
                </Grid>
              )}
              {!isDetailHidden(hideSetting, 'description') && (
                <Box
                  sx={{
                    fontSize: '9.7pt',
                    color: theme.palette.neutral.dark,
                    whiteSpace: 'pre-wrap',
                    textAlign: 'justify',
                  }}
                >
                  {chooseDBTranslation(i18n, item).description}
                </Box>
              )}
            </Grid>
          )
        })

      case 'personCompetences':
        if (features.includes('competences')) {
          return data.personCompetences.map((item: IPersonCompetence, index: number) => {
            let date = ''
            if (item.issueDate) {
              date += `${convertToDisplayDate(item.issueDate, item.dateResolution)} - `
            }
            if (item.endDate) {
              date += `${convertToDisplayDate(item.endDate, item.dateResolution)}`
            }

            return (
              <Grid key={item.id} sx={{ width: '100%', marginBottom: '20pt' }}>
                {index === 0 ? (
                  <Grid
                    sx={{
                      fontSize: '18.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 800,
                      marginBottom: '8pt',
                      color: theme.palette.neutral.dark,
                      letterSpacing: 0,
                      borderBottom: `2pt solid ${theme.palette.neutral.dark}`,
                      marginTop: '18pt',
                      minWidth: '100%',
                    }}
                    style={{ color: headingColor ? headingColor : colors.secondary }}
                  >
                    {t(`print.competences.title`)}
                  </Grid>
                ) : null}
                <Grid
                  sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', justifyContent: 'space-between' }}
                >
                  {!isDetailHidden(hideSetting, 'name') && (
                    <Box
                      sx={{
                        fontSize: '11.8pt',
                        fontFamily: printFontFamily,
                        fontWeight: 'bold',
                        lineHeight: 1.7,
                        marginRight: '5px',
                        color: theme.palette.neutral.dark,
                      }}
                    >
                      {item.Competence && chooseDBTranslation(i18n, item.Competence).name}
                    </Box>
                  )}
                  {!isDetailHidden(hideSetting, 'date') && (
                    <Box sx={{ fontSize: '10.1pt', lineHeight: 1.5 }}>{date}</Box>
                  )}
                </Grid>
                {!isDetailHidden(hideSetting, 'issuer') && (
                  <Grid
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'nowrap',
                      marginRight: '15pt',
                      minWidth: '100%',
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: '11.8pt',
                        fontFamily: printFontFamily,
                        fontWeight: 'bold',
                        lineHeight: 1.7,
                        marginRight: '5px',
                        color: theme.palette.neutral.dark,
                      }}
                    >
                      {t('print.issuer')}:{' '}
                    </Box>
                    <Box sx={{ fontSize: '11.8pt', color: theme.palette.neutral.dark, lineHeight: 1.7 }}>
                      {chooseDBTranslation(i18n, item).issuer}
                    </Box>
                  </Grid>
                )}
                {!isDetailHidden(hideSetting, 'description') && (
                  <Box
                    sx={{
                      fontSize: '9.7pt',
                      color: theme.palette.neutral.dark,
                      whiteSpace: 'pre-wrap',
                      textAlign: 'justify',
                    }}
                  >
                    {chooseDBTranslation(i18n, item).description}
                  </Box>
                )}
              </Grid>
            )
          })
        } else {
          return []
        }

      case 'references':
        return data.references.map((item: IReference, index: number) => {
          return (
            <Grid key={item.id}>
              {index === 0 ? (
                <Grid
                  sx={{
                    fontSize: '18.8pt',
                    fontFamily: printFontFamily,
                    fontWeight: 800,
                    marginBottom: '8pt',
                    color: theme.palette.neutral.dark,
                    letterSpacing: 0,
                    borderBottom: `2pt solid ${theme.palette.neutral.dark}`,
                    marginTop: '18pt',
                    minWidth: '100%',
                  }}
                  style={{ color: headingColor ? headingColor : colors.secondary }}
                >
                  {t('print.reference.title')}
                </Grid>
              ) : null}
              {!isDetailHidden(hideSetting, 'name') && (
                <Grid
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    marginRight: '15pt',
                    minWidth: '100%',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {t('print.reference.referee')}:{' '}
                  </Box>
                  <Box
                    sx={{ fontSize: '11.8pt', color: theme.palette.neutral.dark, lineHeight: 1.7 }}
                  >{`${item.firstName} ${item.lastName}`}</Box>
                </Grid>
              )}
              {!isDetailHidden(hideSetting, 'company') && (
                <Grid
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    marginRight: '15pt',
                    minWidth: '100%',
                  }}
                >
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {t('print.reference.company')}:{' '}
                  </Box>
                  <Box sx={{ fontSize: '11.8pt', color: theme.palette.neutral.dark, lineHeight: 1.7 }}>
                    {item.company}
                  </Box>
                </Grid>
              )}
              {!isDetailHidden(hideSetting, 'letter') && (
                <Box
                  sx={{
                    fontSize: '9.7pt',
                    color: theme.palette.neutral.dark,
                    whiteSpace: 'pre-wrap',
                    textAlign: 'justify',
                  }}
                >
                  {chooseDBTranslation(i18n, item).letter}
                </Box>
              )}
            </Grid>
          )
        })

      case 'leisureActivities':
        return data.leisureActivities.map((item: ILeisureActivity, index: number) => {
          let date = ''
          if (item.startDate) {
            date += `${convertToDisplayDate(item.startDate, item.dateResolution)} - `
          }
          if (item.endDate) {
            date += `${convertToDisplayDate(item.endDate, item.dateResolution)}`
          }

          return (
            <Grid key={item.id} sx={{ width: '100%', marginBottom: '20pt' }}>
              {index === 0 ? (
                <Grid
                  sx={{
                    fontSize: '18.8pt',
                    fontFamily: printFontFamily,
                    fontWeight: 800,
                    marginBottom: '8pt',
                    color: theme.palette.neutral.dark,
                    letterSpacing: 0,
                    borderBottom: `2pt solid ${theme.palette.neutral.dark}`,
                    marginTop: '18pt',
                    minWidth: '100%',
                  }}
                  style={{ color: headingColor ? headingColor : colors.secondary }}
                >
                  {t('print.leisureActivities.title')}
                </Grid>
              ) : null}
              <Grid sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', justifyContent: 'space-between' }}>
                {!isDetailHidden(hideSetting, 'name') && (
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {chooseDBTranslation(i18n, item).name}
                  </Box>
                )}
                {!!date && !isDetailHidden(hideSetting, 'date') && (
                  <Box sx={{ fontSize: '10.1pt', lineHeight: 1.5 }}>{date}</Box>
                )}
              </Grid>
              {!!chooseDBTranslation(i18n, item).activityType && !isDetailHidden(hideSetting, 'activityType') && (
                <Grid sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap', minWidth: '100%' }}>
                  <Box
                    sx={{
                      fontSize: '11.8pt',
                      fontFamily: printFontFamily,
                      fontWeight: 'bold',
                      lineHeight: 1.7,
                      marginRight: '5px',
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    {t('print.leisureActivities.type')}:
                  </Box>
                  <Box sx={{ fontSize: '11.8pt', color: theme.palette.neutral.dark, lineHeight: 1.7 }}>
                    {chooseDBTranslation(i18n, item).activityType}
                  </Box>
                </Grid>
              )}
              {!isDetailHidden(hideSetting, 'description') && (
                <Box
                  sx={{
                    fontSize: '9.7pt',
                    color: theme.palette.neutral.dark,
                    whiteSpace: 'pre-wrap',
                    textAlign: 'justify',
                  }}
                >
                  {chooseDBTranslation(i18n, item).description}
                </Box>
              )}
            </Grid>
          )
        })

      case 'urls':
        return data.urls.map((item: IUrl, index: number) => {
          return (
            <Grid key={item.id} sx={{ width: '100%', marginBottom: '20pt' }}>
              {index === 0 ? (
                <Grid
                  sx={{
                    fontSize: '18.8pt',
                    fontFamily: printFontFamily,
                    fontWeight: 800,
                    marginBottom: '8pt',
                    color: theme.palette.neutral.dark,
                    letterSpacing: 0,
                    borderBottom: `2pt solid ${theme.palette.neutral.dark}`,
                    marginTop: '18pt',
                    minWidth: '100%',
                  }}
                  style={{ color: headingColor ? headingColor : colors.secondary }}
                >
                  {t('print.links.title')}
                </Grid>
              ) : null}
              <Grid
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  marginRight: '15pt',
                  minWidth: '100%',
                }}
              >
                <Box sx={{ fontSize: '11.8pt', color: theme.palette.neutral.dark, lineHeight: 1.7 }}>{item.url}</Box>
              </Grid>
              <Box
                sx={{
                  fontSize: '9.7pt',
                  color: theme.palette.neutral.dark,
                  whiteSpace: 'pre-wrap',
                  textAlign: 'justify',
                }}
              >
                {chooseDBTranslation(i18n, item).description}
              </Box>
            </Grid>
          )
        })

      default:
        break
    }
  }, [
    data.courses,
    data.degrees,
    data.employments,
    data.personCertificates,
    data.personCompetences,
    data.projects,
    data.references,
    data.leisureActivities,
    data.urls,
    features,
    headingColor,
    hideSetting,
    i18n,
    layout,
    t,
    theme.palette.neutral.dark,
    type,
  ])

  if (hidden) {
    return <></>
  }

  return <Grid sx={{ paddingTop: '10pt', paddingLeft: '20pt', paddingRight: '20pt', fontSize: 10 }}>{items}</Grid>
}

export default DataLayout
